import { UAParser } from 'ua-parser-js';
import type { Device, Fingerprint, Screen, UTMParams } from '../types';

export function collectFingerprint(): Fingerprint {
  if (typeof window === 'undefined') {
    return {
      userAgent: '',
      acceptLanguage: '',
      timezone: '',
      cookieEnabled: false,
      doNotTrack: false,
    };
  }

  return {
    userAgent: navigator.userAgent,
    acceptLanguage: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack === '1',
  };
}

export function detectDevice(): Device {
  if (typeof window === 'undefined') {
    return {
      type: 'desktop',
      browser: 'Unknown',
      os: 'Unknown',
    };
  }

  const parser = new UAParser(navigator.userAgent);
  const result = parser.getResult();

  // Determine device type
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (result.device.type === 'mobile') {
    deviceType = 'mobile';
  } else if (result.device.type === 'tablet') {
    deviceType = 'tablet';
  }

  return {
    type: deviceType,
    browser: result.browser.name || 'Unknown',
    browserVersion: result.browser.version,
    os: result.os.name || 'Unknown',
    osVersion: result.os.version,
  };
}

export function getScreenInfo(): Screen {
  if (typeof window === 'undefined') {
    return {
      width: 0,
      height: 0,
      colorDepth: 0,
      devicePixelRatio: 1,
    };
  }

  return {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    devicePixelRatio: window.devicePixelRatio || 1,
  };
}

export function parseUtmParams(url?: string): UTMParams {
  if (typeof window === 'undefined' && !url) {
    return {
      source: null,
      medium: null,
      campaign: null,
      content: null,
      term: null,
    };
  }

  const searchParams = new URLSearchParams(
    url ? new URL(url).search : window.location.search
  );

  return {
    source: searchParams.get('utm_source'),
    medium: searchParams.get('utm_medium'),
    campaign: searchParams.get('utm_campaign'),
    content: searchParams.get('utm_content'),
    term: searchParams.get('utm_term'),
  };
}

export function classifyReferrer(referrer: string): string {
  if (!referrer) return 'direct';

  try {
    const domain = new URL(referrer).hostname.toLowerCase();

    const searchEngines = ['google', 'bing', 'yahoo', 'duckduckgo', 'baidu'];
    const socialSites = [
      'facebook',
      'twitter',
      'linkedin',
      'instagram',
      'pinterest',
      'reddit',
      'tiktok',
    ];

    if (searchEngines.some((engine) => domain.includes(engine))) {
      return 'search';
    }

    if (socialSites.some((social) => domain.includes(social))) {
      return 'social';
    }

    return 'referral';
  } catch {
    return 'direct';
  }
}

export function getPageLoadTime(): number | null {
  if (typeof window === 'undefined' || !window.performance?.timing) {
    return null;
  }

  const timing = window.performance.timing;
  if (timing.loadEventEnd === 0) {
    return null;
  }

  return timing.loadEventEnd - timing.navigationStart;
}

export function shouldRespectDoNotTrack(): boolean {
  if (typeof window === 'undefined') return false;
  return navigator.doNotTrack === '1';
}
